if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getSelectedUpsells() {
        try {
          if (!this.form?.getAttribute('id')?.startsWith('product-form-')) return [];

          const productInfo = this.closest('product-info');
          if (!productInfo) return [];

          const items = [];
          const seen = new Set();

          productInfo
            .querySelectorAll('product-recommendations.snc-upsell .snc-upsell__checkbox:checked:not(:disabled)')
            .forEach((checkbox) => {
              const id = Number(checkbox.dataset.variantId);
              const quantity = Math.max(1, Number(checkbox.dataset.quantity) || 1);
              if (!id || seen.has(id)) return;
              seen.add(id);
              items.push({ id, quantity });
            });

          return items;
        } catch (error) {
          // Jamais bloquant : on retombe sur un ajout du produit principal seul.
          console.warn('SNC upsell: selection ignoree suite a une erreur.', error);
          return [];
        }
      }

      unlockSubmitButton() {
        this.submitButton.classList.remove('loading');
        this.submitButton.removeAttribute('aria-disabled');
        this.querySelector('.loading__spinner')?.classList.add('hidden');
      }

      buildMainCartItem(formData) {
        const item = {
          id: Number(formData.get('id')),
          quantity: Math.max(1, Number(formData.get('quantity')) || 1),
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = Number(sellingPlan) || sellingPlan;

        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (!match || value === '' || (typeof File !== 'undefined' && value instanceof File)) continue;
          properties[match[1]] = value;
        }
        if (Object.keys(properties).length) item.properties = properties;

        return item;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        let formData;
        let mainItem;
        let isMultiAdd;
        let config;

        // Tout ce bloc est synchrone : une exception ici laisserait le bouton
        // verrouille en aria-disabled pour toujours. On garantit le deverrouillage.
        try {
          formData = new FormData(this.form);
          mainItem = this.buildMainCartItem(formData);
          const upsellItems = this.getSelectedUpsells().filter((item) => item.id !== mainItem.id);
          isMultiAdd = upsellItems.length > 0;

          if (isMultiAdd) {
            config = fetchConfig('json');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';

            const body = { items: [mainItem, ...upsellItems] };
            if (this.cart) {
              body.sections = this.cart.getSectionsToRender().map((section) => section.id);
              body.sections_url = window.location.pathname;
              this.cart.setActiveElement(document.activeElement);
            }
            config.body = JSON.stringify(body);
          } else {
            config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];

            if (this.cart) {
              formData.append(
                'sections',
                this.cart.getSectionsToRender().map((section) => section.id)
              );
              formData.append('sections_url', window.location.pathname);
              this.cart.setActiveElement(document.activeElement);
            }
            config.body = formData;
          }
        } catch (error) {
          console.error("Product form: preparation de l'ajout au panier echouee.", error);
          this.handleErrorMessage(window.cartStrings?.error || 'Nie udało się dodać produktów do koszyka.');
          this.unlockSubmitButton();
          return;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || response.message);

              if (isMultiAdd) {
                this.error = false;
                return;
              }

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (isMultiAdd && Array.isArray(response.items) && !response.id) {
              response.id = response.items[0]?.id || mainItem.id;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(window.cartStrings?.error || 'Nie udało się dodać produktów do koszyka.');
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}

(() => {
  if (window.__sncComplementaryUpsellReady) return;
  window.__sncComplementaryUpsellReady = true;

  const ROOT_SELECTOR = 'product-recommendations.complementary-products';
  const productCache = new Map();

  const getProductData = (productUrl) => {
    const parsed = new URL(productUrl, window.location.origin);
    parsed.search = '';
    parsed.hash = '';
    parsed.pathname = `${parsed.pathname.replace(/\.js$/, '').replace(/\/$/, '')}.js`;
    const endpoint = parsed.toString();

    if (!productCache.has(endpoint)) {
      productCache.set(
        endpoint,
        fetch(endpoint, { headers: { Accept: 'application/json' } })
          .then((response) => {
            if (!response.ok) throw new Error(`Upsell product request failed: ${response.status}`);
            return response.json();
          })
          .catch((error) => {
            productCache.delete(endpoint);
            throw error;
          })
      );
    }

    return productCache.get(endpoint);
  };

  const createChoice = (title) => {
    const label = document.createElement('label');
    label.className = 'snc-upsell__choice';

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'snc-upsell__checkbox';
    input.disabled = true;
    input.setAttribute('aria-label', `Dodaj ${title} do zamówienia`);

    const box = document.createElement('span');
    box.className = 'snc-upsell__box';
    box.setAttribute('aria-hidden', 'true');

    label.append(input, box);
    return { label, input };
  };

  const prepareItem = async (item) => {
    if (item.dataset.sncUpsellPreparing === 'true' || item.classList.contains('snc-upsell__item')) return;
    item.dataset.sncUpsellPreparing = 'true';

    const card = item.querySelector('.card-wrapper');
    if (!card) {
      delete item.dataset.sncUpsellPreparing;
      return;
    }

    const links = Array.from(card.querySelectorAll('.card__heading a[href]'));
    const titleLink = links[links.length - 1] || card.querySelector('a[href*="/products/"]');
    if (!titleLink) {
      delete item.dataset.sncUpsellPreparing;
      return;
    }

    const title = titleLink.textContent.trim();
    const productUrl = titleLink.href;
    const sourceImage = card.querySelector('.card__media img');
    const sourcePrice = card.querySelector('.card-information .price') || card.querySelector('.price');

    const shell = document.createElement('div');
    shell.className = 'snc-upsell__card';

    const { label, input } = createChoice(title);
    shell.appendChild(label);

    const mediaLink = document.createElement('a');
    mediaLink.className = 'snc-upsell__media';
    mediaLink.href = productUrl;
    mediaLink.setAttribute('aria-label', title);
    if (sourceImage) {
      const image = sourceImage.cloneNode(true);
      image.className = 'snc-upsell__image';
      image.loading = 'lazy';
      mediaLink.appendChild(image);
    }
    shell.appendChild(mediaLink);

    const meta = document.createElement('div');
    meta.className = 'snc-upsell__meta';

    const cleanTitleLink = document.createElement('a');
    cleanTitleLink.className = 'snc-upsell__title';
    cleanTitleLink.href = productUrl;
    cleanTitleLink.textContent = title;
    meta.appendChild(cleanTitleLink);

    const variantNote = document.createElement('span');
    variantNote.className = 'snc-upsell__variant-note';
    variantNote.hidden = true;
    meta.appendChild(variantNote);

    if (sourcePrice) {
      const priceWrap = document.createElement('div');
      priceWrap.className = 'snc-upsell__price';
      priceWrap.appendChild(sourcePrice.cloneNode(true));
      meta.appendChild(priceWrap);
    }

    shell.appendChild(meta);
    item.replaceChildren(shell);
    item.classList.add('snc-upsell__item');

    item.addEventListener('click', (event) => {
      if (event.target.closest('a, input, label, button') || input.disabled) return;
      input.click();
    });

    try {
      const productData = await getProductData(productUrl);
      const variants = Array.isArray(productData.variants) ? productData.variants : [];
      const availableVariants = variants.filter((variant) => variant.available);
      const variant = availableVariants[0] || variants[0];

      if (!variant || !variant.available) {
        item.classList.add('snc-upsell__item--unavailable');
        variantNote.textContent = 'Niedostępny';
        variantNote.hidden = false;
        return;
      }

      if (variants.length > 1) {
        item.classList.add('snc-upsell__item--options');
        variantNote.textContent = 'Wybierz wariant na stronie produktu';
        variantNote.hidden = false;
        return;
      }

      input.dataset.variantId = String(variant.id);
      input.dataset.quantity = String(variant.quantity_rule?.min || 1);
      input.disabled = false;
    } catch (error) {
      console.warn('SNC complementary upsell:', error);
      item.classList.add('snc-upsell__item--unavailable');
      variantNote.textContent = 'Produkt chwilowo niedostępny';
      variantNote.hidden = false;
    }
  };

  const decorate = (root) => {
    const container = root.querySelector('.complementary-products__container');
    if (!container) return;

    root.classList.add('snc-upsell');

    const summary = container.querySelector('.summary__title');
    if (summary && !container.querySelector('.snc-upsell__subtitle')) {
      const subtitle = document.createElement('p');
      subtitle.className = 'snc-upsell__subtitle';
      subtitle.textContent = 'Uzupełnij kompozycję o pasujące dodatki.';
      summary.insertAdjacentElement('afterend', subtitle);
    }

    container.querySelectorAll('.complementary-slide > ul > li').forEach((item) => prepareItem(item));
  };

  const watch = (root) => {
    if (root.dataset.sncUpsellObserved === 'true') return;
    root.dataset.sncUpsellObserved = 'true';

    const observer = new MutationObserver(() => decorate(root));
    observer.observe(root, { childList: true, subtree: true });
    decorate(root);
  };

  const discover = () => document.querySelectorAll(ROOT_SELECTOR).forEach(watch);

  const pageObserver = new MutationObserver(discover);
  pageObserver.observe(document.documentElement, { childList: true, subtree: true });
  discover();
})();